import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { userService, UserProfile, type AccountHealth } from '@/services/userService';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';
import { registerUserProfileUpdateCallback, unregisterUserProfileUpdateCallback } from '@/services/httpClient';

interface UserProfileData {
  name: string;
  email: string;
  timezone: string;
  avatar?: string;
  accountHealth?: AccountHealth | null;
  isDemo?: boolean;
}

interface UserProfileContextType {
  userData: UserProfileData;
  isLoading: boolean;
  error: string | null;
  refetchProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

// Global cache to prevent multiple calls
let cachedUserData: UserProfileData | null = null;
let isCurrentlyFetching = false;
let globalRefetchCallback: (() => Promise<void>) | null = null;

// Export function to trigger refetch from anywhere
export const triggerProfileRefetch = async (): Promise<void> => {
  if (globalRefetchCallback) {
    await globalRefetchCallback();
  }
};

// Export function to clear cached user profile data (used on logout)
export const clearUserProfileCache = (): void => {
  cachedUserData = null;
  isCurrentlyFetching = false;
  // Note: globalRefetchCallback is NOT cleared here so that
  // triggerProfileRefetch() still works after logout→login cycles.
};

export const UserProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserProfileData>(() => {
    return cachedUserData || { name: 'Loading...', email: '', timezone: 'UTC' };
  });
  const [isLoading, setIsLoading] = useState(true); // Always start as loading to ensure fetch
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Function to update user data from external source (HTTP interceptor)
  const updateUserDataFromProfile = useCallback((profile: UserProfile) => {
    if (!profile || !profile.id || !profile.name || !profile.email) {
      return;
    }

    const newUserData = {
      name: profile.name,
      email: profile.email,
      timezone: profile.timezone || 'UTC',
      avatar: profile.avatarUrl || undefined,
      accountHealth: profile.accountHealth ?? null,
      isDemo: profile.isDemo ?? false,
    };

    cachedUserData = newUserData;

    if (isMountedRef.current) {
      setUserData({ ...newUserData });
      setError(null);
      setIsLoading(false);
    }
  }, []);

  const fetchUserData = useCallback(async (force = false) => {
    // When force=true (after login), check token directly instead of relying on stale React state
    const hasValidToken = force ? authService.isAuthenticated() : isAuthenticated;

    if (!hasValidToken) {
      cachedUserData = null;
      setUserData({ name: 'User', email: 'user@example.com', timezone: 'UTC', avatar: undefined });
      setIsLoading(false);
      return;
    }

    // If already fetching and not forced, skip
    if (isCurrentlyFetching && !force) {
      return;
    }

    try {
      setIsLoading(true);
      isCurrentlyFetching = true;
      
      // Clear cache when force is true (after login)
      if (force) {
        cachedUserData = null;
      }

      const profile = await userService.getProfile();

      if (profile && profile.id && profile.name && profile.email) {
        const newUserData = {
          name: profile.name,
          email: profile.email,
          timezone: profile.timezone || 'UTC',
          avatar: profile.avatarUrl || undefined,
          accountHealth: profile.accountHealth ?? null,
          isDemo: profile.isDemo ?? false,
        };

        cachedUserData = newUserData;

        if (isMountedRef.current) {
          setUserData({ ...newUserData });
          setError(null);
        }
      } else {
        throw new Error('Invalid profile data received');
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      const fallbackData = {
        name: 'Error loading profile',
        email: 'error@example.com',
        timezone: 'UTC',
        avatar: undefined
      };

      if (isMountedRef.current) {
        setUserData(fallbackData);
        setError('Failed to load user profile');
      }
    } finally {
      isCurrentlyFetching = false;
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  // Handle authentication state changes (single unified useEffect)
  useEffect(() => {
    // Clear state when logged out
    if (!isAuthenticated) {
      cachedUserData = null;
      isCurrentlyFetching = false;
      setUserData({ name: 'User', email: 'user@example.com', timezone: 'UTC', avatar: undefined });
      setIsLoading(false);
      return;
    }

    // Fetch user data when authenticated
    // Call directly - the token should already be set when isAuthenticated becomes true
    fetchUserData(true);
  }, [isAuthenticated, fetchUserData]);

  const refetchProfile = useCallback(async () => {
    // Check token directly instead of relying on potentially stale React state
    if (!authService.isAuthenticated()) {
      return;
    }

    // Force refetch to bypass cache
    await fetchUserData(true);
  }, [fetchUserData]);

  // Register global refetch callback
  useEffect(() => {
    globalRefetchCallback = refetchProfile;
    return () => {
      globalRefetchCallback = null;
    };
  }, [refetchProfile]);

  // Register HTTP interceptor callback to auto-update on /api/users/me calls
  useEffect(() => {
    registerUserProfileUpdateCallback(updateUserDataFromProfile);
    return () => {
      unregisterUserProfileUpdateCallback();
    };
  }, [updateUserDataFromProfile]);

  return (
    <UserProfileContext.Provider value={{ userData, isLoading, error, refetchProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
